/* eslint-disable react-hooks/exhaustive-deps */
import { TSelectedSkuItem, TService } from "@elixir/types";
import React from "react";
import { Autosuggest } from "ui";
import usePostCreateService from "../../apis/axon/post-create-service";
import useDebounce from "../../hooks/use-debounce";
import { GetServiceAutosuggest } from "../../types/apis";
import { TBillingServiceSearchInputProps } from "../billing-queue/service-autosuggest/billing-service-search-input";
import { TBillingServiceSearchPopupProps } from "../billing-queue/service-autosuggest/billing-service-search-popup";
import useGetServicesMasterAutosuggest from "../../apis/axon/services/use-get-services-master-autosuggest";
import {
  getServiceDropdownId,
  getServiceIdAndPriceFromServiceDropdownId,
} from "@elixir/utils";

export type TServicesAutosuggestProps = {
  isDisabled?: boolean;
  currService?: TSelectedSkuItem;
  multipleServiceNames?: string;
  onServiceValueInputChange?: (newInputValue: string) => void;
  onServiceSelection: ({ service }: { service: TSelectedSkuItem }) => void;
  SearchInputComponent: (props: TBillingServiceSearchInputProps) => JSX.Element;
  SearchPopupComponent: (props: TBillingServiceSearchPopupProps) => JSX.Element;
  inputPlaceholder?: string;
};

const ServicesAutosuggest = ({
  isDisabled,
  currService,
  multipleServiceNames,
  onServiceSelection,
  onServiceValueInputChange,
  SearchInputComponent,
  SearchPopupComponent,
  inputPlaceholder,
}: TServicesAutosuggestProps) => {
  const [inputValue, setInputValue] = React.useState<string>(
    () => currService?.service_name || ""
  );

  React.useEffect(() => {
    const newVal = currService?.service_name || "";
    if (newVal !== inputValue) {
      setInputValue(currService?.service_name || "");
    }
  }, [currService]);

  const handleValueChange = (newValue: string) => {
    setInputValue(newValue);
  };

  const debouncedInputValue = useDebounce(inputValue, 300);

  const {
    data: ownerServicesAutosuggestData,
    isLoading: isServiceAutosuggestLoading,
  } = useGetServicesMasterAutosuggest({
    query: debouncedInputValue,
    pageSize: 100,
  });

  const [servicesSuggestionList, setServicesSuggestionList] = React.useState<
    TSelectedSkuItem[]
  >(() => []);

  React.useEffect(() => {
    const setServices = async () => {
      setServicesSuggestionList(ownerServicesAutosuggestData?.data || []);
    };

    if (!isServiceAutosuggestLoading) {
      setServices();
    }
  }, [
    debouncedInputValue,
    isServiceAutosuggestLoading,
    ownerServicesAutosuggestData?.data,
  ]);

  const searchOptions = React.useMemo(() => {
    return servicesSuggestionList.map((service) => ({
      name: service.service_name,
      id: getServiceDropdownId(service),
    }));
  }, [servicesSuggestionList]);

  const handleSuggestionSelect = (
    id: string | number,
    servicesList?: TSelectedSkuItem[]
  ) => {
    const { service_id, price } = getServiceIdAndPriceFromServiceDropdownId(
      `${id}`
    );
    const serviceSelected = (servicesList || servicesSuggestionList).find(
      (service) => service.service_id === service_id && service.price === price
    );

    if (serviceSelected) {
      onServiceSelection({ service: serviceSelected });
    }
  };

  const handleNewCreateService = async ({
    newService,
  }: {
    newService: GetServiceAutosuggest;
  }) => {
    if (newService) {
      const newServiceData: TService = {
        price: 0,
        service_id: Number(newService.id),
        service_name: newService.name,
      };
      setServicesSuggestionList([...servicesSuggestionList, newServiceData]);

      handleSuggestionSelect(getServiceDropdownId(newServiceData), [
        ...servicesSuggestionList,
        newServiceData,
      ]);
    }
  };

  React.useEffect(() => {
    onServiceValueInputChange?.(inputValue);
  }, [inputValue]);

  const { mutateAsync: createServiceType, isLoading } = usePostCreateService();

  return (
    <Autosuggest
      inputValue={inputValue}
      isDisabled={isDisabled}
      handleValueChange={handleValueChange}
      options={searchOptions}
      onSuggestSelect={handleSuggestionSelect}
      SearchInputComponent={(props) => (
        <SearchInputComponent
          {...props}
          multipleServiceNames={multipleServiceNames}
          placeholder={inputPlaceholder}
          inputValue={inputValue}
        />
      )}
      SearchPopupComponent={(props) => (
        <SearchPopupComponent
          {...props}
          services={servicesSuggestionList}
          handleNewCreateService={async ({ serviceName }) => {
            if (!isLoading && inputValue) {
              const { data } = await createServiceType({
                name: serviceName,
              });

              if (data) {
                const { handleInputStatus } = props;
                handleNewCreateService({ newService: data });
                handleInputStatus(false);
              }
            }
          }}
        />
      )}
    />
  );
};

export default ServicesAutosuggest;
